import React, { useCallback } from 'react';
import { Card } from 'antd';
import { Phone, Mail, Users, TrendingUp, TrendingDown, Activity, UserCheck } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { formatShortNumber } from '../../utils/number';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { ContactCompanyStats } from './types';

dayjs.extend(quarterOfYear);
dayjs.extend(weekOfYear);

interface EffortsSectionProps {
  callsData: any[];
  emailsData: any[];
  contactCompanyStats: ContactCompanyStats;
  callsLoading: boolean;
  emailsLoading: boolean;
  contactCompanyStatsLoading: boolean;
  callsError: string | null;
  emailsError: string | null;
  contactCompanyStatsError: string | null;
  periodLabel: string;
  groupBy: 'day' | 'month' | 'week';
  dateRange: { startDate: string; endDate: string };
  isUserFiltered?: boolean;
}

// Modern Metric Card with Comparison
const MetricComparisonCard: React.FC<{
  title: string;
  current: number;
  target: number;
  previous?: number;
  icon: React.ReactNode;
  iconBg: string;
  unit?: string;
  type: 'calls' | 'emails';
  loading?: boolean;
  showTarget?: boolean;
}> = ({ title, current, target, previous, icon, iconBg, unit = '', type, loading = false, showTarget = true }) => {
  const percentage = target > 0 ? Math.round((current / target) * 100) : 0;
  const previousComparison = previous ? ((current - previous) / previous * 100) : null;
  
  const getStatusColor = (perc: number) => {
    if (perc >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (perc >= 70) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (perc >= 50) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const progressColor = percentage >= 90 ? '#10b981' : percentage >= 70 ? '#3b82f6' : percentage >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-2 rounded-lg ${iconBg}`}>
          {icon}
        </div>
        {showTarget && !loading && (
          <div className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(percentage)}`}>
            {percentage}%
          </div>
        )}
      </div>
      
      <div className="mb-4">
        {loading ? (
          <div className="animate-pulse">
            <div className="h-7 w-24 bg-slate-200 rounded mb-2" />
            <div className="h-4 w-32 bg-slate-200 rounded" />
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-bold text-slate-900 mb-1">
              {current.toLocaleString()}{unit}
            </h3>
            <p className="text-slate-600 text-sm font-medium">{title}</p>
          </>
        )}
      </div>

      {/* Progress Bar */}
      {showTarget && (
        <div className="mb-4">
          {loading ? (
            <div className="space-y-2">
              <div className="h-3 w-20 bg-slate-200 rounded" />
              <div className="w-full bg-slate-200 rounded-full h-2" />
            </div>
          ) : (
            <>
              <div className="flex justify-between text-xs text-slate-600 mb-1">
                <span>Progress</span>
                <span>{current} / {target}</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${Math.min(percentage, 100)}%`,
                    backgroundColor: progressColor
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Comparison with Previous Period */}
      {showTarget && !loading && previousComparison !== null && (
        <div className="flex items-center gap-2">
          {previousComparison > 0 ? (
            <div className="flex items-center gap-1 text-green-600">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm font-medium">+{previousComparison.toFixed(1)}%</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-red-600">
              <TrendingDown className="h-4 w-4" />
              <span className="text-sm font-medium">{previousComparison.toFixed(1)}%</span>
            </div>
          )}
          <span className="text-xs text-slate-500">vs last period</span>
        </div>
      )}

      {/* Target/Shortfall removed (progress already shows it) */}
    </div>
  );
};

// Contact Statistics Donut Chart
const ContactStatsDonut: React.FC<{
  stats: ContactCompanyStats;
  loading: boolean;
}> = ({ stats, loading }) => {
  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Contact Status</h3>
        </div>
        <div className="animate-pulse">
          <div className="w-48 h-48 bg-slate-200 rounded-full mx-auto mb-4" />
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-4 bg-slate-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const contactedCount = (stats.total_count || 0) - (stats.not_contacted_count || 0);
  const neverContactedCount = stats.not_contacted_count || 0;
  const notContactedIn3Months = stats.not_contacted_in_3months || 0;

  const companiesUrl = `${process.env.REACT_APP_ENTITY_URL}/companies`;
  
  const data = [
    { 
      name: 'Recently Contacted', 
      value: Math.max(0, contactedCount - notContactedIn3Months), 
      color: '#10b981',
      percentage: Math.round(((contactedCount - notContactedIn3Months) / (stats.total_count || 1)) * 100),
      url: `${companiesUrl}?lastContactedOperator=greaterThan&lastContactedFrom=${dayjs().subtract(3, 'month').format('YYYY-MM-DD')}`
    },
    { 
      name: 'Not Contacted (3+ months)', 
      value: notContactedIn3Months, 
      color: '#f59e0b',
      percentage: Math.round((notContactedIn3Months / (stats.total_count || 1)) * 100),
      url: `${companiesUrl}?lastContactedOperator=lessThan&lastContactedFrom=${dayjs().subtract(3, 'month').format('YYYY-MM-DD')}`
    },
    { 
      name: 'Never Contacted', 
      value: neverContactedCount, 
      color: '#ef4444',
      percentage: Math.round((neverContactedCount / (stats.total_count || 1)) * 100),
      url: `${companiesUrl}?lastContactedOperator=notContacted`
    },
  ].filter(item => item.value > 0);

  const renderLabel = (entry: any) => `${entry.percentage}%`;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <UserCheck className="h-5 w-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Contact Status</h3>
      </div>

      <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="w-40 h-40 sm:w-48 sm:h-48">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
                label={renderLabel}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: any, name: any) => [`${value} Companies`, name]} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex-1 w-full lg:ml-6">
          <div className="space-y-4">
            {data.map((item, index) => (
              <a 
                key={index} 
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between p-2 -mx-2 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <div>
                    <p className="text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                      {item.name}
                    </p>
                    <p className="text-xs text-slate-600">{item.percentage}%</p>
                  </div>
                </div>
                <span className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                  {item.value}
                </span>
              </a>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm font-medium text-slate-700">Total Companies</span>
              <span className="text-lg font-bold text-slate-900">{stats.total_count || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Performance Chart Component
const PerformanceChart: React.FC<{
  data: any[];
  loading: boolean;
  error: string | null;
  type: 'calls' | 'emails';
  title: string;
  perDayTarget: number;
  dateRange?: { startDate: string; endDate: string };
  labelFormatter?: (value: any) => string;
  tickFormatter?: (value: any) => string;
  showTarget?: boolean;
}> = ({ data, loading, error, type, title, perDayTarget, dateRange, labelFormatter, tickFormatter, showTarget = true }) => {
  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-slate-200 rounded mb-4" />
          <div className="h-64 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">{title}</h3>
        <div className="text-center py-8">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const chartData = data.map((item, idx) => {
    const curr = dayjs(item.date);
    const next = data[idx + 1] ? dayjs(data[idx + 1].date) : undefined;
    let days = 1;
    if (next) {
      const diff = next.diff(curr, 'day');
      days = Math.max(diff, 1);
    } else if (dateRange) {
      // For the last bucket, estimate remaining days using the selected range
      const end = dayjs(dateRange.endDate);
      const diff = end.diff(curr, 'day') + 1;
      days = Math.max(diff, 1);
    }
    const actual = type === 'calls'
      ? (item.total_calls ?? item.total ?? 0)
      : (item.total_emails ?? item.total ?? 0);
    const target = perDayTarget * days;
    return { date: item.date, actual, target };
  });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 sm:p-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-6">{title}</h3>
      
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickFormatter={tickFormatter}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickFormatter={(v)=> formatShortNumber(Number(v))}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              labelFormatter={labelFormatter}
            />
            <Legend />
            {showTarget && (
              <Bar 
                dataKey="target" 
                fill="#e2e8f0" 
                name="Target"
                radius={[2, 2, 0, 0]}
              />
            )}
            <Bar 
              dataKey="actual" 
              fill={type === 'calls' ? '#3b82f6' : '#10b981'} 
              name="Actual"
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const EffortsSection: React.FC<EffortsSectionProps> = ({
  callsData,
  emailsData,
  contactCompanyStats,
  callsLoading,
  emailsLoading,
  contactCompanyStatsLoading,
  callsError,
  emailsError,
  contactCompanyStatsError,
  periodLabel,
  groupBy,
  dateRange,
  isUserFiltered = false,
}) => {

  // Calculate current period totals
  const currentCallsTotal = callsData.reduce((sum, item) => sum + (item.total_calls ?? item.total ?? 0), 0);
  const currentEmailsTotal = emailsData.reduce((sum, item) => sum + (item.total_emails ?? item.total ?? 0), 0);

  // Targets: 50 per day for both calls and emails based on selected date range
  const daysInRange = Math.max(dayjs(dateRange.endDate).diff(dayjs(dateRange.startDate), 'day') + 1, 1);
  const callsTarget = 50 * daysInRange;
  const emailsTarget = 50 * daysInRange;

  // Label/tick formatters inspired by DynamicChart
  const getWeekOfMonth = useCallback((d: dayjs.Dayjs) => {
    const firstDay = d.startOf('month');
    const firstDayMondayBased = (firstDay.day() + 6) % 7;
    const daysFromWeekStart = d.date() - 1 + firstDayMondayBased;
    return Math.floor(daysFromWeekStart / 7) + 1;
  }, []);

  const labelFormatter = useCallback((value: any) => {
    const d = dayjs(value);
    if (groupBy === 'month') return d.format('MMM YYYY');
    if (groupBy === 'week') return `Week ${getWeekOfMonth(d)}`;
    return d.format('MMM DD, YYYY');
  }, [groupBy, getWeekOfMonth]);

  const tickFormatter = useCallback((value: any) => {
    const d = dayjs(value);
    if (groupBy === 'month') return d.format('MMM');
    if (groupBy === 'week') return `W${getWeekOfMonth(d)}`;
    return d.format('MMM DD');
  }, [groupBy, getWeekOfMonth]);
  
  // Previous period comparison (mock data - you'd get this from API)
  const previousCallsTotal = Math.floor(currentCallsTotal * (0.8 + Math.random() * 0.4));
  const previousEmailsTotal = Math.floor(currentEmailsTotal * (0.8 + Math.random() * 0.4));

  return (
    <Card className="rounded-lg shadow-sm">
      <div className="p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Activity className="h-6 w-6 text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Efforts & Activities</h2>
          <span className="ml-2 text-xs font-medium px-2 py-1 bg-violet-600 text-white rounded-full">
            {periodLabel}
          </span>
        </div>

        {/* Metrics + Contact Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="space-y-6">
            <MetricComparisonCard
              title="Calls Made"
              current={currentCallsTotal}
              target={callsTarget}
              previous={previousCallsTotal}
              icon={<Phone className="h-5 w-5 text-blue-600" />}
              iconBg="bg-blue-100"
              type="calls"
              loading={callsLoading}
              showTarget={isUserFiltered}
            />
            <MetricComparisonCard
              title="Emails Sent"
              current={currentEmailsTotal}
              target={emailsTarget}
              previous={previousEmailsTotal}
              icon={<Mail className="h-5 w-5 text-green-600" />}
              iconBg="bg-green-100"
              type="emails"
              loading={emailsLoading}
              showTarget={isUserFiltered}
            />
          </div>
          <div className="lg:col-span-2">
            <ContactStatsDonut 
              stats={contactCompanyStats} 
              loading={contactCompanyStatsLoading} 
            />
          </div>
        </div>

        {/* Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PerformanceChart
            data={callsData}
            loading={callsLoading}
            error={callsError}
            type="calls"
            title="Call Performance Trend"
            perDayTarget={50}
            dateRange={dateRange}
            labelFormatter={labelFormatter}
            tickFormatter={tickFormatter}
            showTarget={isUserFiltered}
          />
          
          <PerformanceChart
            data={emailsData}
            loading={emailsLoading}
            error={emailsError}
            type="emails"
            title="Email Performance Trend"
            perDayTarget={50}
            dateRange={dateRange}
            labelFormatter={labelFormatter}
            tickFormatter={tickFormatter}
            showTarget={isUserFiltered}
          />
        </div>

        {/* Effort Status Summary */}
        {isUserFiltered ? (
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="flex flex-col gap-4 text-left md:flex-row md:items-center md:justify-between">
              <div>
                <h4 className="font-semibold text-blue-900">Overall Effort Status</h4>
                <p className="text-sm text-blue-700">
                  {currentCallsTotal >= callsTarget && currentEmailsTotal >= emailsTarget
                    ? "🎉 Excellent! All targets achieved"
                    : currentCallsTotal >= callsTarget * 0.8 || currentEmailsTotal >= emailsTarget * 0.8
                    ? "📈 Good progress, keep pushing!"
                    : "⚠️ Needs attention - increase activity"}
                </p>
              </div>
              <div className="text-left md:text-right">
                <div className="text-2xl font-bold text-blue-900">
                  {Math.round(((currentCallsTotal / callsTarget + currentEmailsTotal / emailsTarget) / 2) * 100)}%
                </div>
                <div className="text-sm text-blue-700">Combined Target</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="flex flex-col gap-4 text-left md:flex-row md:items-center md:justify-between">
              <div>
                <h4 className="font-semibold text-blue-900">Overall Activity</h4>
                <p className="text-sm text-blue-700">Team totals for the selected period</p>
              </div>
              <div className="text-left text-sm text-blue-900 md:text-right">
                <div>Calls: <span className="font-semibold">{currentCallsTotal.toLocaleString()}</span></div>
                <div>Emails: <span className="font-semibold">{currentEmailsTotal.toLocaleString()}</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default EffortsSection;